import { useState, useEffect, useCallback } from 'react';

interface UseTableStateOptions {
  defaultSortField: string;
  defaultSortDir?: 'asc' | 'desc';
  pageSize?: number;
  searchDebounceMs?: number;
}

export interface UseTableStateReturn {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  sortField: string;
  sortDir: 'asc' | 'desc';
  searchQuery: string;
  debouncedSearch: string;
  pageSize: number;
  ordering: string;
  onSort: (field: string) => void;
  onSearchChange: (query: string) => void;
  onPageChange: (page: number) => void;
  setTotalCount: (count: number) => void;
  resetPage: () => void;
}

export function useTableState(options: UseTableStateOptions): UseTableStateReturn {
  const { defaultSortField, defaultSortDir = 'asc', pageSize = 20, searchDebounceMs = 0 } = options;

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCountState] = useState(0);
  const [sortField, setSortField] = useState(defaultSortField);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(defaultSortDir);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchState, setDebouncedSearchState] = useState('');

  useEffect(() => {
    if (searchDebounceMs === 0) return;
    const timer = setTimeout(() => setDebouncedSearchState(searchQuery), searchDebounceMs);
    return () => clearTimeout(timer);
  }, [searchQuery, searchDebounceMs]);

  const debouncedSearch = searchDebounceMs === 0 ? searchQuery : debouncedSearchState;

  const onSort = useCallback((field: string) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setCurrentPage(1);
  }, [sortField]);

  const onSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  }, []);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const setTotalCount = useCallback(
    (count: number) => {
      setTotalCountState(count);
      setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
    },
    [pageSize],
  );

  const resetPage = useCallback(() => {
    setCurrentPage(1);
  }, []);

  const ordering = `${sortDir === 'desc' ? '-' : ''}${sortField}`;

  return {
    currentPage,
    totalPages,
    totalCount,
    sortField,
    sortDir,
    searchQuery,
    debouncedSearch,
    pageSize,
    ordering,
    onSort,
    onSearchChange,
    onPageChange,
    setTotalCount,
    resetPage,
  };
}
